/**
 * 영업 메일 유입 표식(`?cm=<historyId>`)의 판정·보관 규칙.
 *
 * 저장소는 인자로 받는다 — 사생활 모드·차단된 저장소에서 접근 자체가 던지므로 모든
 * 접근을 감싸고 실패는 「없음」으로 접는다. 보관은 30일, **먼저 온 값이 이긴다**.
 */

/** 유입 표식을 담는 쿼리 이름 — 메일 링크가 `?cm=<historyId>` 로 단다 */
export const OUTREACH_QUERY_KEY = 'cm';

/** 보관 키(로컬 저장소) — `{ id, at }` JSON */
export const OUTREACH_REF_KEY = 'cm.outreach';

/** 브라우저 한 대를 가리키는 id 의 보관 키 — admin 의 중복 판정 키가 쓴다 */
export const OUTREACH_SID_KEY = 'cm.sid';

/** 보관 기한 30일 — 지나면 없는 것으로 친다 */
export const OUTREACH_REF_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** 저장소 최소 계약 — `localStorage`·`sessionStorage` 가 이 모양이다 */
export type OutreachStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

/** 보관된 유입 표식 — `at` 은 ISO 문자열 */
export type OutreachRef = { id: string; at: string };

/** 메일 유입으로 세지 않는 경로 — 수신거부 링크도 같은 `?cm=` 을 달고 오지만 관심 표시가 아니다 */
export const OUTREACH_UNTRACKED_PATH_PREFIXES: readonly string[] = ['/unsubscribe'];

/** 이 경로의 진입을 메일 유입(클릭 보고 · 활동 로그)으로 세는가 */
export function isOutreachTrackedPath(pathname: string): boolean {
  return !OUTREACH_UNTRACKED_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/** admin 이 발급한 발송 이력 id 의 모양인가 */
export function isValidOutreachId(value: unknown): value is string {
  return typeof value === 'string' && ID_PATTERN.test(value);
}

/**
 * 주소의 쿼리에서 유입 표식을 꺼낸다.
 * @param search `?cm=...` 또는 `cm=...`
 * @returns 모양에 맞는 표식 · 없거나 불량이면 null
 */
export function parseOutreachIdFromSearch(search: string): string | null {
  try {
    const value = new URLSearchParams(search).get(OUTREACH_QUERY_KEY);
    return isValidOutreachId(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * 보관된 표식을 읽는다.
 * @returns 30일을 넘겼거나 읽을 수 없으면 null
 */
export function readStoredOutreachRef(storage: OutreachStorage, now: number = Date.now()): OutreachRef | null {
  const raw = readStorageItem(storage, OUTREACH_REF_KEY);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const ref = parsed as Partial<OutreachRef> | null;
  if (!ref || !isValidOutreachId(ref.id) || typeof ref.at !== 'string') return null;
  const at = Date.parse(ref.at);
  if (Number.isNaN(at) || now - at >= OUTREACH_REF_TTL_MS) return null;
  return { id: ref.id, at: ref.at };
}

/**
 * 표식을 보관한다 — **살아 있는 보관본이 있으면 그것을 지킨다**(먼저 온 값이 이긴다).
 * @returns 보관 결과(기존 것일 수 있다) · 표식이 불량이면 null
 */
export function storeOutreachRef(
  storage: OutreachStorage,
  id: string,
  now: number = Date.now()
): OutreachRef | null {
  if (!isValidOutreachId(id)) return null;
  const kept = readStoredOutreachRef(storage, now);
  if (kept) return kept;
  const ref: OutreachRef = { id, at: new Date(now).toISOString() };
  writeStorageItem(storage, OUTREACH_REF_KEY, JSON.stringify(ref));
  return ref;
}

/** 보관된 표식을 지운다 — 가입 보고가 끝난 뒤 부른다 */
export function clearStoredOutreachRef(storage: OutreachStorage): void {
  try {
    storage.removeItem(OUTREACH_REF_KEY);
  } catch {
    // 저장소가 막힌 환경 — 지울 것도 없다
  }
}

/** 브라우저 한 대의 id — 없으면 만들어 보관한다 */
export function getOrCreateOutreachSid(storage: OutreachStorage): string {
  const kept = readStorageItem(storage, OUTREACH_SID_KEY);
  if (kept && kept.length <= MAX_SID_LENGTH) return kept;
  const sid = randomSid();
  writeStorageItem(storage, OUTREACH_SID_KEY, sid);
  return sid;
}

/** 세션당 1회 보고를 가리는 플래그 키 */
export function sessionClickKey(id: string): string {
  return `cm.click.${id}`;
}

/**
 * 이 세션에서 아직 보고하지 않았으면 표를 찍고 참을 준다.
 * @returns 지금 보고해야 하면 true · 이미 보고했으면 false
 */
export function takeSessionClickOnce(storage: OutreachStorage, id: string): boolean {
  const key = sessionClickKey(id);
  if (readStorageItem(storage, key)) return false;
  writeStorageItem(storage, key, '1');
  return true;
}

/**
 * 브라우저 저장소를 집는다 — 속성 접근 자체가 던지는 환경(사생활 모드·차단)을 접는다.
 * @returns 쓸 수 없으면 null(서버 렌더 포함)
 */
export function readBrowserStorage(kind: 'local' | 'session'): OutreachStorage | null {
  if (typeof window === 'undefined') return null;
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

/** 저장소 읽기 — 접근이 던지면(차단·사생활 모드) null */
export function readStorageItem(storage: OutreachStorage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/** 저장소 쓰기 — 용량 초과·차단은 조용히 넘어간다 */
export function writeStorageItem(storage: OutreachStorage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // 용량 초과·차단 — 보관은 부가 기능이라 조용히 넘어간다
  }
}

// ---- 사적 부품 ---------------------------------------------------------

const ID_PATTERN = /^[A-Za-z0-9_-]{6,64}$/;
const MAX_SID_LENGTH = 64;

function randomSid(): string {
  try {
    const uuid = globalThis.crypto?.randomUUID?.();
    if (uuid) return uuid;
  } catch {
    // randomUUID 가 없거나 보안 컨텍스트가 아닌 환경 — 아래 대체값으로
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
